import React, { useEffect, useState } from "react";
import {
  ArcElement,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  Tooltip
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import LoadingComponent from "../../../common/loading/loading.component";
import { useGetDeviceTypeChartQuery } from "../../../../app/services/chatbot-rtk.services";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

interface ChartProps {
  from: string;
  to: string;
  botId: string;
}

const DeviceTypeChart: React.FC<ChartProps> = ({ from, to, botId }) => {
  const { t, i18n } = useTranslation();
  const { data, error, isLoading, isFetching } = useGetDeviceTypeChartQuery({
    from,
    to,
    bot_id: botId
  });
  const [chartData, setChartData] = useState<any>(null);
  const [count, setCount] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Explicitly type the options
  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom", // Use allowed position values
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14
          }
        }
      }
    },
    cutout: "70%" // Hollow center
  };

  useEffect(() => {
    setCount(0);
    setChartData(null);
    if (data) {
      const Mobile =
        data.find((dr: any) => dr.category == "Mobile")?.percentage ?? 0;

      if (Mobile) {
        setIsMobile(true);
        setCount(Math.round(Mobile));
      } else {
        const Desktop =
          data.find((dr: any) => dr.category == "Desktop")?.percentage ?? 0;
        setCount(Math.round(Desktop));
      }

      setChartData({
        labels: data.map((dr: any) => dr.category),
        datasets: [
          {
            label: "Device Types",
            data: data.map((dr: any) => +dr.percentage),
            backgroundColor: [
              "#FF6384",
              "#36A2EB",
              "#FFCE56",
              "#4BC0C0",
              "#9966FF"
            ]
          }
        ]
      });
    }
  }, [data, isFetching]);

  if (isLoading) return <LoadingComponent />;
  if (error) return <LoadingComponent />;
  return (
    <div className="tw-flex tw-flex-col  tw-items-center tw-justify-center">
      <div className="tw-flex tw-flex-col tw-min-w-[237px] tw-items-center tw-justify-center">
        <div className="tw-text-gray-800 tw-text-base tw-font-semibold tw-font-[Lexend] tw-uppercase">
          {t("Charts/DeviceType/title")}
        </div>
        {count !== 0 ? (
          <div className="tw-text-gray-800 tw-text-3xl tw-font-medium tw-font-['Lexend']">
            {count + "%"}
          </div>
        ) : (
          <div className="tw-text-gray-400 tw-text-xl tw-font-medium tw-font-['Lexend']">
            {t("Charts/NoData")}
          </div>
        )}
        <div className="tw-text-gray-800 tw-text-base tw-font-normal tw-font-[Lexend]">
          {count !== 0 && (
            <>
              {isMobile ? (
                <>{t("Charts/DeviceType/description")}</>
              ) : (
                <>Desktop</>
              )}
            </>
          )}
        </div>
      </div>
      <div>
        {!chartData && <LoadingComponent />}
        {chartData && count !== 0 && (
          <div className="tw-h-[250px] tw-mt-6">
            <Doughnut data={chartData} options={options} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceTypeChart;
